import { z } from "zod";
import { AssetMediaDto } from "./asset-media.ts";
import { CompositionAspectRatioDto } from "./aspect-ratio.ts";
import { DfyCouponDto } from "./coupon.ts";
import { SourceImageDto } from "./source-image.ts";
import {
  VideoRenderStatus,
  VideoRenderUpscaleQuality,
} from "./video-render.ts";
import { CreationSource } from "./analytics.ts";

export const DfyOrderType = z.enum(["SMALL", "MEDIUM", "LARGE"]);

export const DfyOrderStatus = z.enum([
  "DRAFT",
  "SUBMITTED",
  "IN_PROGRESS",
  "DELIVERED",
]);

export const DfyImageSelectionMode = z.enum(["USER", "EDITOR"]);

export const DfyTemplateType = z.enum([
  "PROPERTY_INTRO",
  "AGENT_OUTRO",
  "LOGO",
]);

export const DfyPaymentMethod = z.enum(["CREDITS", "STRIPE"]);

export const DfyOrderTemplate = z.object({
  type: DfyTemplateType,
});

export const DfyAddOns = z.object({
  lifestyle: z.boolean(),
  virtualStaging: z.boolean(),
  furnitureReveal: z.boolean(),
  export4k: z.boolean(),
  aiLabel: z.boolean(),
  branding: z.boolean(),
});

export const DfySourceImageConfig = z.object({
  externalId: z.string(),
  isSelected: z.boolean(),
});

export const DfyPropertyDetails = z.object({
  address: z.string(),
  highlights: z.string(),
});

export const DfyAgentDetails = z.object({
  name: z.string(),
  title: z.string(),
  company: z.string(),
  phone: z.string(),
  email: z.string(),
  headshotAssetMediaExternalId: z.string().nullable(),
});

export const DfyOrderDetailsDto = z.object({
  type: DfyOrderType,
  addOns: DfyAddOns,
  listingUrl: z.string(),
  imageSelectionMode: DfyImageSelectionMode,
  sourceImageConfigs: z.array(DfySourceImageConfig),
  templates: z.array(DfyOrderTemplate),
  propertyDetails: DfyPropertyDetails,
  agentDetails: DfyAgentDetails,
  logoAssetMediaExternalId: z.string().nullable(),
  freeText: z.string(),
  paymentMethod: DfyPaymentMethod,
  couponCode: z.string().nullable().default(null),
});

export const DfyOrderLineItem = z.object({
  id: z.string(),
  label: z.string(),
  credits: z.number(),
});

export const DfyOrderPaymentDetails = z.object({
  paymentMethod: DfyPaymentMethod,
  lineItems: z.array(DfyOrderLineItem),
  coupon: DfyCouponDto.nullable().default(null),
  creditsFromBalance: z.number(),
  stripeCredits: z.number(),
});

export const DfyVideoRenderDto = z.object({
  externalId: z.string(),
  externalFileName: z.string(),
  createdAt: z.string(),
  status: VideoRenderStatus,
  downloadUrl: z.string().nullable(),
  aspectRatio: CompositionAspectRatioDto.nullable(),
  upscaleQuality: VideoRenderUpscaleQuality.nullable(),
});

export const DfyDeliveryDto = z.object({
  externalId: z.string(),
  name: z.string(),
  isVisibleForCustomer: z.boolean(),
  thumbnailTimeMs: z.number().nullable(),
  createdAt: z.string(),
  videoRender: DfyVideoRenderDto,
});

export const CreateDfyOrderDto = z.object({
  source: CreationSource.optional(),
});

export const DfyOrderDto = z.object({
  externalId: z.string(),
  status: DfyOrderStatus,
  details: DfyOrderDetailsDto,
  paymentDetails: DfyOrderPaymentDetails.nullable(),
  createdAt: z.string(),
  submittedAt: z.string().nullable(),
});

export const DfyOrderListItemDto = z.object({
  externalId: z.string(),
  type: DfyOrderType,
  status: DfyOrderStatus,
  createdAt: z.string(),
  submittedAt: z.string().nullable(),
  thumbnailSrc: z.string().nullable(),
});

export const DfyOrderWithAssetsDto = z.object({
  order: DfyOrderDto,
  submittedByEmail: z.string().nullable(),
  sourceImages: z.array(SourceImageDto),
  assetMedias: z.array(AssetMediaDto),
  deliveries: z.array(DfyDeliveryDto),
});

export const DfyOrderListResponseDto = z.object({
  orders: z.array(DfyOrderListItemDto),
});

export const DfyOrderSort = z.enum(["newest", "oldest"]);

export const GetDfyEditorOrdersQuery = z.object({
  page: z.coerce.number().int().min(0).max(1000000).default(0),
  status: DfyOrderStatus.optional(),
  sort: DfyOrderSort.default("newest"),
  search: z.string().trim().optional(),
});

export const PagedDfyOrderResponseDto = DfyOrderListResponseDto.extend({
  nextPage: z.number().optional(),
});

export const UpdateDfyOrderDetailsDto = z.object({
  details: DfyOrderDetailsDto,
});

export const UpdateDfyOrderStatusDto = z.object({
  status: DfyOrderStatus,
  /** Sends the in-app notification and email. Only applies when the status changes to DELIVERED. Defaults to true. */
  notifyCustomer: z.boolean().optional(),
});

export const SubmitDfyOrderDto = z.object({
  paymentMethod: DfyPaymentMethod,
});

export const SubmitDfyOrderResponseDto = z.discriminatedUnion("type", [
  z.object({ type: z.literal("SUBMITTED"), order: DfyOrderDto }),
  z.object({ type: z.literal("CHECKOUT"), checkoutUrl: z.string() }),
]);

export const CreateDfyDeliveryDto = z.object({
  videoRenderExternalId: z.string(),
  name: z.string().trim().optional(),
});

export const UpdateDfyDeliveryDto = z.object({
  name: z.string().trim().optional(),
  isVisibleForCustomer: z.boolean().optional(),
  thumbnailTimeMs: z.number().int().min(0).optional(),
});

export const CreateVideoFromDfyOrderResponseDto = z.object({
  videoExternalId: z.string(),
  folderExternalId: z.string(),
});

export const GetDfyAssetMediaDownloadUrlResponseDto = z.object({
  downloadUrl: z.string(),
});

export const DfyEditorVideoRenderDto = z.object({
  videoRender: DfyVideoRenderDto,
  videoExternalId: z.string(),
  isInOrderFolder: z.boolean(),
});

export const DfyEditorVideoRendersResponseDto = z.object({
  videoRenders: z.array(DfyEditorVideoRenderDto),
});

// "folder" = only renders from the order's editor folder, "all" = folder renders first, then the rest of the team.
export const DfyEditorVideoRendersScope = z.enum(["folder", "all"]);

export const GetDfyEditorVideoRendersQuery = z.object({
  page: z.coerce.number().int().min(0).max(1000000).default(0),
  scope: DfyEditorVideoRendersScope.default("all"),
});

export const PagedDfyEditorVideoRendersResponseDto =
  DfyEditorVideoRendersResponseDto.extend({
    nextPage: z.number().optional(),
  });

export type DfyOrderType = z.infer<typeof DfyOrderType>;
export type DfyOrderStatus = z.infer<typeof DfyOrderStatus>;
export type DfyImageSelectionMode = z.infer<typeof DfyImageSelectionMode>;
export type DfyTemplateType = z.infer<typeof DfyTemplateType>;
export type DfyOrderTemplate = z.infer<typeof DfyOrderTemplate>;
export type DfyAddOns = z.infer<typeof DfyAddOns>;
export type DfySourceImageConfig = z.infer<typeof DfySourceImageConfig>;
export type DfyPropertyDetails = z.infer<typeof DfyPropertyDetails>;
export type DfyAgentDetails = z.infer<typeof DfyAgentDetails>;
export type DfyOrderDetailsDto = z.infer<typeof DfyOrderDetailsDto>;
export type DfyOrderLineItem = z.infer<typeof DfyOrderLineItem>;
export type DfyOrderPaymentDetails = z.infer<typeof DfyOrderPaymentDetails>;
export type DfyVideoRenderDto = z.infer<typeof DfyVideoRenderDto>;
export type DfyDeliveryDto = z.infer<typeof DfyDeliveryDto>;
export type CreateDfyOrderDto = z.infer<typeof CreateDfyOrderDto>;
export type DfyOrderDto = z.infer<typeof DfyOrderDto>;
export type DfyOrderListItemDto = z.infer<typeof DfyOrderListItemDto>;
export type DfyOrderWithAssetsDto = z.infer<typeof DfyOrderWithAssetsDto>;
export type DfyOrderListResponseDto = z.infer<typeof DfyOrderListResponseDto>;
export type DfyOrderSort = z.infer<typeof DfyOrderSort>;
export type GetDfyEditorOrdersQuery = z.infer<typeof GetDfyEditorOrdersQuery>;
export type PagedDfyOrderResponseDto = z.infer<typeof PagedDfyOrderResponseDto>;
export type UpdateDfyOrderDetailsDto = z.infer<typeof UpdateDfyOrderDetailsDto>;
export type UpdateDfyOrderStatusDto = z.infer<typeof UpdateDfyOrderStatusDto>;
export type DfyPaymentMethod = z.infer<typeof DfyPaymentMethod>;
export type SubmitDfyOrderDto = z.infer<typeof SubmitDfyOrderDto>;
export type SubmitDfyOrderResponseDto = z.infer<
  typeof SubmitDfyOrderResponseDto
>;
export type CreateDfyDeliveryDto = z.infer<typeof CreateDfyDeliveryDto>;
export type UpdateDfyDeliveryDto = z.infer<typeof UpdateDfyDeliveryDto>;
export type CreateVideoFromDfyOrderResponseDto = z.infer<
  typeof CreateVideoFromDfyOrderResponseDto
>;
export type GetDfyAssetMediaDownloadUrlResponseDto = z.infer<
  typeof GetDfyAssetMediaDownloadUrlResponseDto
>;
export type DfyEditorVideoRenderDto = z.infer<typeof DfyEditorVideoRenderDto>;
export type DfyEditorVideoRendersResponseDto = z.infer<
  typeof DfyEditorVideoRendersResponseDto
>;
export type DfyEditorVideoRendersScope = z.infer<
  typeof DfyEditorVideoRendersScope
>;
export type GetDfyEditorVideoRendersQuery = z.infer<
  typeof GetDfyEditorVideoRendersQuery
>;
export type PagedDfyEditorVideoRendersResponseDto = z.infer<
  typeof PagedDfyEditorVideoRendersResponseDto
>;
